/**
 * A random id per device that Gatekeeper's percentage restraint buckets on
 * (VIP-12592), so a percentage rollout or A/B test keeps a visitor in the
 * same group across logins and IP changes. It lives in a first-party cookie
 * on the shop domain, which every API request carries, so api-v2 buckets on
 * it wherever it asks Gatekeeper (features, payment methods). It is strictly
 * necessary for feature bucketing only: do not hand it to tracking.
 */
import CookieHelper from '@/services/helpers/CookieHelper'

/** Cookie holding the device's visitor id; api-v2 reads the same name */
export const VISITOR_ID_COOKIE = 'gatekeeper_visitor_id'

// Chrome caps cookie lifetimes at 400 days and Safari caps cookies set from
// script at 7, so the expiry is renewed on every visit
const LIFETIME_DAYS = 400

// Same shape api-v2 accepts; anything else in the cookie is replaced
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const _randomUuid = (): string => {
  if (typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  // randomUUID needs a secure context; build the same version 4 UUID by hand
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))
  return [
    hex.slice(0, 4),
    hex.slice(4, 6),
    hex.slice(6, 8),
    hex.slice(8, 10),
    hex.slice(10, 16)
  ]
    .map((group) => group.join(''))
    .join('-')
}

/**
 * Makes sure the device's visitor id cookie exists and renews its expiry.
 * Returns the id, or null where cookies are blocked: then no id is sent and
 * api-v2 keeps bucketing on email or IP, which at least stays the same across
 * reloads. Also null during a server render, which has no device to name.
 */
export const ensureVisitorId = (): string | null => {
  if (import.meta.env.SSR || !globalThis.document || !globalThis.crypto) {
    return null
  }

  try {
    const stored = CookieHelper.getCookie(VISITOR_ID_COOKIE)
    const visitorId = UUID_REGEX.test(stored) ? stored : _randomUuid()
    CookieHelper.setCookie(VISITOR_ID_COOKIE, visitorId, LIFETIME_DAYS)
    return CookieHelper.getCookie(VISITOR_ID_COOKIE) === visitorId
      ? visitorId
      : null
  } catch {
    return null
  }
}
